import React from 'react';
import { Box, Typography } from '@mui/material';
import { Text, View } from '@react-pdf/renderer';
import {
  buildPvmLeftColumnClusters,
  PVM_LEFT_SECTION_HEADERS,
  type PvmKeyValueRow,
} from './pvmLeftColumnLayout';
import { PVM_TEXT_COLOR, withPvmBodyStyle } from './pvmBranding';

const webRowSx = {
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'flex-start' as const,
  py: 0,
  lineHeight: 1.02,
  fontSize: 11,
};

const webLabelSx = {
  width: '42%',
  flexShrink: 0,
  color: PVM_TEXT_COLOR,
  pr: 0.75,
  whiteSpace: 'pre-line' as const,
  wordBreak: 'break-word' as const,
  overflowWrap: 'anywhere' as const,
};
const webValueSx = { flex: 1, width: '58%', color: PVM_TEXT_COLOR, whiteSpace: 'pre-line' as const, minWidth: 0, lineHeight: 1.02 };

const proseSx = withPvmBodyStyle(
  {
    fontSize: 11,
    color: PVM_TEXT_COLOR,
    lineHeight: 1.05,
    textAlign: 'justify' as const,
    mb: 0,
  },
  true,
);

function WebSection({
  title,
  rows,
  titleSx,
  compact,
}: {
  title: string;
  rows: PvmKeyValueRow[];
  titleSx: object;
  compact?: boolean;
}) {
  if (!rows.length) return null;
  return (
    <Box sx={{ mb: compact ? 0.35 : 2 }}>
      <Typography sx={{ ...titleSx, mb: compact ? 0.05 : 0.75 }}>{title}</Typography>
      {rows.map((row) => (
        <Box key={row.label} sx={webRowSx}>
          <Box sx={webLabelSx}>{row.label}</Box>
          <Box sx={webValueSx}>{row.value}</Box>
        </Box>
      ))}
    </Box>
  );
}

function WebProseSection({
  id,
  title,
  body,
  titleSx,
  compact,
  onTitleClick,
}: {
  id: string;
  title: string;
  body: string;
  titleSx: object;
  compact?: boolean;
  onTitleClick?: () => void;
}) {
  if (!body.trim()) return null;
  return (
    <Box id={id} sx={{ mb: compact ? 0.35 : 2 }}>
      <Typography sx={{ ...titleSx, mb: compact ? 0.05 : 0.75 }} onClick={onTitleClick}>
        {title}
      </Typography>
      <Box sx={proseSx}>{body}</Box>
    </Box>
  );
}

export const PvmPage1LeftColumnWeb: React.FC<{
  pdf: Record<string, unknown>;
  contentTitle2Sx: (template?: unknown) => object | ((theme?: unknown) => object);
  template?: unknown;
  compact?: boolean;
  investmentphilosophieTitle?: string;
  strategieTitle?: string;
  onTitleClick?: () => void;
}> = ({
  pdf,
  contentTitle2Sx,
  template,
  compact,
  investmentphilosophieTitle = 'INVESTMENTPHILOSOPHIE',
  strategieTitle = 'STRATEGIE',
  onTitleClick,
}) => {
  const clusters = buildPvmLeftColumnClusters(pdf);
  const titleSx =
    typeof contentTitle2Sx === 'function'
      ? contentTitle2Sx(template)
      : contentTitle2Sx;
  const investmentphilosophie = String(pdf?.investmentphilosophie ?? '');
  const strategie = String(pdf?.strategie ?? '');

  return (
    <Box id="pvm-left-column-clusters">
      <WebProseSection
        id="investmentphilosophie-section"
        title={investmentphilosophieTitle}
        body={investmentphilosophie}
        titleSx={titleSx}
        compact={compact}
        onTitleClick={onTitleClick}
      />
      <WebProseSection
        id="strategie-section"
        title={strategieTitle}
        body={strategie}
        titleSx={titleSx}
        compact={compact}
        onTitleClick={onTitleClick}
      />
      <WebSection title={PVM_LEFT_SECTION_HEADERS.fondsdetails} rows={clusters.fondsdetails} titleSx={titleSx} compact={compact} />
      <WebSection title={PVM_LEFT_SECTION_HEADERS.konditionen} rows={clusters.konditionen} titleSx={titleSx} compact={compact} />
      <WebSection title={PVM_LEFT_SECTION_HEADERS.fondspartner} rows={clusters.fondspartner} titleSx={titleSx} compact={compact} />
    </Box>
  );
};

export const PvmPage1LeftColumnPdf: React.FC<{
  pdf: Record<string, unknown>;
  pdfStyles: any;
  templateColor: string;
  renderContentHeader: (title: string, color?: string) => React.ReactNode;
  investmentphilosophieTitle?: string;
  strategieTitle?: string;
}> = ({
  pdf,
  pdfStyles,
  templateColor,
  renderContentHeader,
  investmentphilosophieTitle = 'INVESTMENTPHILOSOPHIE',
  strategieTitle = 'STRATEGIE',
}) => {
  const clusters = buildPvmLeftColumnClusters(pdf);
  const styles = pdfStyles;
  const investmentphilosophie = String(pdf?.investmentphilosophie ?? '').trim();
  const strategie = String(pdf?.strategie ?? '').trim();

  const renderRows = (rows: PvmKeyValueRow[]) =>
    rows.map((row) => (
      <View key={row.label} style={styles.keyValueRow} wrap>
        <Text style={styles.keyValueKey}>{row.label}</Text>
        <Text style={[styles.keyValueValue, { lineHeight: 0.92, flex: 1 }]}>{row.value}</Text>
      </View>
    ));

  const renderSection = (title: string, rows: PvmKeyValueRow[], keepTogether = false) => {
    if (!rows.length) return null;
    return (
      <View style={styles.leftColumnSection} wrap={!keepTogether}>
        {renderContentHeader(title, templateColor)}
        {renderRows(rows)}
      </View>
    );
  };

  return (
    <>
      {investmentphilosophie ? (
        <View style={styles.leftColumnSection}>
          {renderContentHeader(investmentphilosophieTitle, templateColor)}
          <Text style={[styles.sectionText, { marginBottom: 0, color: PVM_TEXT_COLOR }]}>
            {investmentphilosophie}
          </Text>
        </View>
      ) : null}
      {strategie ? (
        <View style={styles.leftColumnSection}>
          {renderContentHeader(strategieTitle, templateColor)}
          <Text style={[styles.sectionText, { marginBottom: 0, color: PVM_TEXT_COLOR }]}>{strategie}</Text>
        </View>
      ) : null}
      {renderSection(PVM_LEFT_SECTION_HEADERS.fondsdetails, clusters.fondsdetails)}
      {renderSection(PVM_LEFT_SECTION_HEADERS.konditionen, clusters.konditionen)}
      {renderSection(PVM_LEFT_SECTION_HEADERS.fondspartner, clusters.fondspartner, true)}
    </>
  );
};
